/**
 * Billing API calls.
 *
 * - New subscriptions: `startCheckout` asks the backend for a Stripe Checkout Session
 *   bound to the signed-in user; `verifyCheckout` confirms it on /billing/success.
 * - Existing subscribers: `openBillingPortal` (cancel, card, invoices) and in-place
 *   Plus <-> Premium switches queued for the end of the current billing period.
 */

import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import type { AuthUser } from "@/hooks/useAuth";
import type { PlanId } from "@/lib/billing";

/** A billing call that failed, with the backend's machine-readable `code` when it sent one. */
export class BillingApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
  }
}

async function toError(res: Response): Promise<BillingApiError> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  return new BillingApiError(body.error ?? `Request failed (${res.status})`, res.status, body.code ?? null);
}

/**
 * Whether new purchases are open right now FOR THIS USER (works signed out too). Sent
 * with the session when there is one, because during a staged rollout the answer is
 * per-user; the server returns only this yes/no, never who else is allowed.
 */
export async function getBillingConfig(): Promise<{ checkoutEnabled: boolean }> {
  const res = await authFetch(apiUrl("/billing/config"));
  if (!res.ok) throw await toError(res);
  return (await res.json()) as { checkoutEnabled: boolean };
}

/** Create a Checkout Session for `plan` and return the Stripe-hosted URL to send the user to. */
export async function startCheckout(plan: Exclude<PlanId, "starter">): Promise<string> {
  const res = await authFetch(apiUrl("/billing/checkout-session"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) throw await toError(res);
  const { url } = (await res.json()) as { url: string };
  return url;
}

/**
 * Confirm a completed checkout server-side (it runs the same sync the webhook does) and
 * get the refreshed user back — so the new plan shows without waiting for the webhook.
 */
export async function verifyCheckout(sessionId: string): Promise<{ user: AuthUser }> {
  const res = await authFetch(apiUrl("/billing/checkout-session/verify"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId }),
  });
  if (!res.ok) throw await toError(res);
  return (await res.json()) as { user: AuthUser };
}

/**
 * Re-read the signed-in user (GET /auth/user) — the success page's read-only fallback
 * while it waits for the plan to land. Never verifies, reconciles or starts anything.
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  const res = await authFetch(apiUrl("/auth/user"), { cache: "no-store" });
  if (!res.ok) throw await toError(res);
  const { user } = (await res.json()) as { user: AuthUser | null };
  return user;
}

/** Stripe Customer Portal URL for the user's subscription (whichever Stripe account it's in). */
export async function openBillingPortal(): Promise<string> {
  const res = await authFetch(apiUrl("/billing/portal-session"), { method: "POST" });
  if (!res.ok) throw await toError(res);
  const { url } = (await res.json()) as { url: string };
  return url;
}

export interface ScheduleChangeResult {
  user: AuthUser;
  /** ISO timestamp the switch takes effect, or null when nothing was scheduled (already on that plan). */
  effectiveAt: string | null;
  alreadyOnPlan?: boolean;
}

/**
 * Queue a switch to `targetPlan` ("plus" or "premium") for the end of the
 * current billing period. Calling this again before the switch lands
 * overwrites the pending target; passing the plan the user is already on
 * cancels a pending switch instead.
 */
export async function schedulePlanChange(targetPlan: PlanId): Promise<ScheduleChangeResult> {
  const res = await authFetch(apiUrl("/billing/schedule-change"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetPlan }),
  });
  if (!res.ok) throw await toError(res);
  return (await res.json()) as ScheduleChangeResult;
}

/** Cancel a pending plan switch, reverting to the current plan indefinitely. */
export async function cancelScheduledChange(): Promise<{ user: AuthUser }> {
  const res = await authFetch(apiUrl("/billing/cancel-scheduled-change"), { method: "POST" });
  if (!res.ok) throw await toError(res);
  return (await res.json()) as { user: AuthUser };
}
